import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Search, ChevronDown, ChevronRight, Package, AlertTriangle, FileText, TrendingUp, Zap, Check } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import QuickQuoteModal from "../components/QuickQuoteModal";

export default function InquiryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [companyId, setCompanyId] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showQuickQuote, setShowQuickQuote] = useState(false);

  useEffect(() => {
    api.get("/admin/companies").then((res) => setCompanies(res.data)).catch(() => {});
    api.get("/inquiry/analytics?days=30").then((res) => setAnalytics(res.data)).catch(() => {});
  }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    try {
      const params = { q: query, search_type: searchType };
      if (companyId) params.company_id = companyId;
      const res = await api.get("/inquiry/search", { params });
      setResults(res.data.results || []);
    } catch { setResults([]); }
    setLoading(false);
  };

  const toggleExpand = (itemId) => {
    setExpandedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const fulfillmentBadge = (status) => {
    const map = {
      WITH_STOCK: { label: "In Stock", cls: "bg-green-100 text-green-800" },
      COMBINED_WAREHOUSE: { label: "Combined", cls: "bg-blue-100 text-blue-800" },
      WITHOUT_STOCK: { label: "No Stock", cls: "bg-red-100 text-red-800" },
      SPECIAL_ORDER: { label: "Special", cls: "bg-purple-100 text-purple-800" },
    };
    const m = map[status] || { label: status, cls: "bg-gray-100 text-gray-700" };
    return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-sm ${m.cls}`}>{m.label}</span>;
  };

  return (
    <div data-testid="inquiry-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>
          Product Inquiry
        </h1>
        <p className="text-sm text-gray-500 mt-1">Search inventory, check stock, and create quotations</p>
      </div>

      <Tabs defaultValue="search">
        <TabsList className="bg-white border border-gray-200 rounded-sm">
          <TabsTrigger value="search" data-testid="inquiry-search-tab" className="rounded-sm data-[state=active]:bg-[#0052CC] data-[state=active]:text-white">
            <Search size={14} className="mr-1.5" /> Search
          </TabsTrigger>
          <TabsTrigger value="analytics" data-testid="inquiry-analytics-tab" className="rounded-sm data-[state=active]:bg-[#0052CC] data-[state=active]:text-white">
            <TrendingUp size={14} className="mr-1.5" /> Analytics
          </TabsTrigger>
        </TabsList>

        <TabsContent value="search" className="mt-4">
          <Card className="border border-gray-200 shadow-sm rounded-sm">
            <CardContent className="p-5">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    data-testid="inquiry-search-input"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search by item code, name, batch, or SKU..."
                    className="pl-9 rounded-sm h-10 border-gray-300"
                  />
                </div>
                <Select value={searchType} onValueChange={setSearchType}>
                  <SelectTrigger data-testid="inquiry-search-type" className="w-[140px] rounded-sm h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Fields</SelectItem>
                    <SelectItem value="item_code">Item Code</SelectItem>
                    <SelectItem value="item_name">Item Name</SelectItem>
                    <SelectItem value="batch">Batch</SelectItem>
                    <SelectItem value="sku">SKU</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={companyId} onValueChange={setCompanyId}>
                  <SelectTrigger data-testid="inquiry-company-filter" className="w-[160px] rounded-sm h-10">
                    <SelectValue placeholder="All Companies" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button data-testid="inquiry-search-button" type="submit" className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm h-10">
                  Search
                </Button>
                {selectedItems.length > 0 && (
                  <Button
                    data-testid="quick-quote-button"
                    type="button"
                    onClick={() => setShowQuickQuote(true)}
                    className="bg-green-600 hover:bg-green-700 rounded-sm h-10"
                  >
                    <Zap size={14} className="mr-1" /> Quick Quote ({selectedItems.length})
                  </Button>
                )}
              </form>
            </CardContent>
          </Card>

          <div className="mt-4 space-y-2">
            {loading && Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-16 rounded-sm" />
            ))}

            {!loading && results.length === 0 && query && (
              <Card className="border border-gray-200 rounded-sm">
                <CardContent className="p-8 text-center">
                  <AlertTriangle size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No results found for "{query}"</p>
                </CardContent>
              </Card>
            )}

            {!loading && results.map((item) => (
              <Card key={item.id} className="border border-gray-200 shadow-sm rounded-sm">
                <CardContent className="p-0">
                  <div
                    data-testid={`inquiry-result-${item.item_code}`}
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <div className="flex items-center gap-3">
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          data-testid={`select-item-${item.item_code}`}
                          checked={selectedItems.some((s) => s.id === item.id)}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedItems((prev) => [...prev, item]);
                            else setSelectedItems((prev) => prev.filter((s) => s.id !== item.id));
                          }}
                        />
                      </div>
                      <div className="w-10 h-10 bg-gray-100 rounded-sm flex items-center justify-center">
                        <Package size={18} className="text-gray-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-800">{item.item_code}</span>
                          {fulfillmentBadge(item.fulfillment_status)}
                        </div>
                        <div className="text-sm text-gray-600">{item.item_name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">
                          SKU: {item.sku || "N/A"} | Category: {item.category || "N/A"} | Unit: {item.unit}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-semibold" style={{ fontFamily: "Chivo, sans-serif" }}>
                          {item.total_available} {item.unit}
                        </div>
                        <div className="text-xs text-gray-500">Available</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-[#0052CC]">{item.base_price?.toFixed(2)}</div>
                        <div className="text-xs text-gray-500">Base Price</div>
                      </div>
                      {expandedItems[item.id] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                  </div>

                  {expandedItems[item.id] && (
                    <div className="border-t border-gray-200 bg-gray-50 p-4">
                      {item.recommended_batch && (
                        <div className="mb-3 text-xs font-medium text-green-700 bg-green-50 px-3 py-1.5 rounded-sm inline-block">
                          FIFO Recommended: {item.recommended_batch}
                        </div>
                      )}
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-500 border-b border-gray-200">
                            <th className="text-left py-2 px-2">Batch</th>
                            <th className="text-left py-2 px-2">Tonality</th>
                            <th className="text-left py-2 px-2">Warehouse</th>
                            <th className="text-right py-2 px-2">Available</th>
                            <th className="text-right py-2 px-2">Committed</th>
                            <th className="text-right py-2 px-2">Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {item.batches.map((batch) => (
                            <tr key={batch.id} className="border-b border-gray-100 last:border-0">
                              <td className="py-2 px-2 font-medium">{batch.batch_code}</td>
                              <td className="py-2 px-2 text-gray-600">{batch.tonality || "—"}</td>
                              <td className="py-2 px-2 text-gray-600">{batch.warehouse}</td>
                              <td className="py-2 px-2 text-right font-medium">{batch.available_qty}</td>
                              <td className="py-2 px-2 text-right text-gray-500">{batch.committed_qty}</td>
                              <td className="py-2 px-2 text-right font-medium">{batch.price?.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-3 flex justify-end">
                        <Button
                          data-testid={`create-quotation-from-${item.item_code}`}
                          size="sm"
                          onClick={() => navigate("/quotations/new", { state: { item } })}
                          className="bg-[#0052CC] hover:bg-[#003D99] rounded-sm"
                        >
                          <FileText size={14} className="mr-1.5" />
                          Create Quotation
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="analytics" className="mt-4">
          {analytics ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2">Total Searches</div>
                <div className="text-3xl font-bold text-[#09090B]" style={{ fontFamily: "Chivo, sans-serif" }}>{analytics.total_searches}</div>
              </CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2">No Results</div>
                <div className="text-3xl font-bold text-red-600" style={{ fontFamily: "Chivo, sans-serif" }}>{analytics.no_results}</div>
              </CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2">Conversions</div>
                <div className="text-3xl font-bold text-green-600" style={{ fontFamily: "Chivo, sans-serif" }}>{analytics.conversions}</div>
              </CardContent></Card>
              <Card className="border border-gray-200 rounded-sm"><CardContent className="p-5">
                <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-2">Conversion Rate</div>
                <div className="text-3xl font-bold text-[#0052CC]" style={{ fontFamily: "Chivo, sans-serif" }}>{analytics.conversion_rate}%</div>
              </CardContent></Card>
              {analytics.top_search_terms?.length > 0 && (
                <Card className="sm:col-span-2 lg:col-span-4 border border-gray-200 rounded-sm"><CardContent className="p-5">
                  <div className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 mb-3">Top Search Terms</div>
                  <div className="flex flex-wrap gap-2">
                    {analytics.top_search_terms.map((t) => (
                      <Badge key={t.term} variant="secondary" className="rounded-sm">
                        {t.term} ({t.count})
                      </Badge>
                    ))}
                  </div>
                </CardContent></Card>
              )}
            </div>
          ) : (
            <Skeleton className="h-32 rounded-sm" />
          )}
        </TabsContent>
      </Tabs>

      <QuickQuoteModal
        open={showQuickQuote}
        onClose={() => setShowQuickQuote(false)}
        selectedItems={selectedItems}
      />
    </div>
  );
}
